import { Component } from '@theme/component';
import { setHeaderMenuStyle } from '@theme/utilities';

const desktopMediaQuery = window.matchMedia('(min-width: 750px)');

/** How long the pointer rests on a closed menu before its panel opens, in ms. */
const OPEN_DELAY = 80;

/** The same while another panel is open, so crossing an item on the way to that panel doesn't swap it. */
const SWITCH_DELAY = 180;

/** How long moving down toward the open panel can hold off a switch, in ms. */
const SWITCH_MAX_DELAY = 400;

/**
 * @typedef {Object} InchbugMegaMenuRefs
 * @property {HTMLDetailsElement[]} [panels] - Desktop megamenu panel disclosures.
 * @property {HTMLElement} [scroller] - The element the menu items overflow.
 */

/**
 * @extends {Component<InchbugMegaMenuRefs>}
 */
class InchbugMegaMenuComponent extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {WeakSet<HTMLDetailsElement>} */
  #boundPanels = new WeakSet();

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #openTimer;

  /** @type {HTMLDetailsElement | null} */
  #pendingPanel = null;

  #pendingDeadline = 0;

  #lastPointerY = 0;

  /** The window width the menu needs to fit. Set while it doesn't. */
  #fitsAtWidth = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#boundPanels = new WeakSet();
    const { signal } = this.#controller;

    this.#bindPanels(signal);
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, { signal });
    document.addEventListener('pointermove', this.#handleDocumentPointerMove, { signal, passive: true });
    document.addEventListener('keydown', this.#handleKeydown, { signal });
    desktopMediaQuery.addEventListener('change', this.#handleBreakpointChange, { signal });
    window.addEventListener('resize', this.#syncOverflow, { signal, passive: true });

    this.#syncOverflow();
    document.fonts?.ready.then(this.#syncOverflow);
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.#controller) this.#bindPanels(this.#controller.signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#boundPanels = new WeakSet();
    this.#cancelOpen();
    this.#cancelClose();
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindPanels(signal) {
    for (const panel of this.refs.panels ?? []) {
      if (!(panel instanceof HTMLDetailsElement) || this.#boundPanels.has(panel)) continue;

      panel.addEventListener('toggle', this.#handlePanelToggle, { signal });
      panel.addEventListener('pointerenter', this.#handlePanelPointerEnter, { signal });
      panel.addEventListener('pointerleave', this.#handlePanelPointerLeave, { signal });
      panel.addEventListener('focusout', this.#handlePanelFocusOut, { signal });
      panel.querySelector(':scope > summary')?.addEventListener('click', this.#handleTriggerClick, { signal });
      this.#boundPanels.add(panel);
    }
  }

  /** @returns {HTMLDetailsElement | undefined} */
  get #openPanel() {
    return (this.refs.panels ?? []).find((panel) => panel instanceof HTMLDetailsElement && panel.open);
  }

  /**
   * @param {Event} event
   */
  #handlePanelToggle = (event) => {
    const panel = event.currentTarget;
    if (!(panel instanceof HTMLDetailsElement)) return;

    panel.querySelector(':scope > summary')?.setAttribute('aria-expanded', panel.open.toString());
    if (panel.open) this.#closePanels(panel);
  };

  /**
   * Hover already opened the panel, so a mouse click must not toggle it shut. Enter and Space send
   * a click with no detail, and those keep the normal toggle.
   * @param {MouseEvent} event
   */
  #handleTriggerClick = (event) => {
    if (!desktopMediaQuery.matches || event.detail === 0) return;
    if (event.target instanceof Element && event.target.closest('a')) return;

    event.preventDefault();
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePanelPointerEnter = (event) => {
    if (!desktopMediaQuery.matches) return;

    this.#cancelClose();
    const panel = event.currentTarget;
    if (!(panel instanceof HTMLDetailsElement)) return;

    if (panel.open) {
      this.#cancelOpen();
      return;
    }

    this.#pendingPanel = panel;
    this.#pendingDeadline = performance.now() + SWITCH_MAX_DELAY;
    this.#queueOpen(this.#openPanel ? SWITCH_DELAY : OPEN_DELAY);
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePanelPointerLeave = (event) => {
    if (!desktopMediaQuery.matches) return;

    if (event.currentTarget === this.#pendingPanel) this.#cancelOpen();
    this.#scheduleClose();
  };

  /**
   * @param {FocusEvent} event
   */
  #handlePanelFocusOut = (event) => {
    if (!desktopMediaQuery.matches) return;

    const panel = event.currentTarget;
    if (!(panel instanceof HTMLDetailsElement)) return;

    // No related target: focus went nowhere, like a click on a blank part of the panel.
    if (!(event.relatedTarget instanceof Node) || panel.contains(event.relatedTarget)) return;

    this.#scheduleClose();
  };

  /**
   * @param {number} delay
   */
  #queueOpen(delay) {
    clearTimeout(this.#openTimer);
    this.#openTimer = setTimeout(() => {
      const panel = this.#pendingPanel;
      this.#pendingPanel = null;
      if (!panel) return;

      this.#closePanels(panel);
      panel.open = true;
    }, delay);
  }

  #cancelOpen() {
    clearTimeout(this.#openTimer);
    this.#pendingPanel = null;
  }

  #scheduleClose() {
    // Keep a running timer: restarting it on every pointer move would keep the panel open.
    if (this.#closeTimer !== undefined) return;
    // A keyboard user is working inside the panel, so a stray mouse move must not close it.
    if (this.#openPanel?.querySelector(':focus-visible')) return;

    this.#closeTimer = setTimeout(() => this.#closePanels(), 140);
  }

  #cancelClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  }

  /**
   * @param {HTMLDetailsElement} [except]
   */
  #closePanels(except) {
    this.#cancelClose();

    for (const panel of this.refs.panels ?? []) {
      if (!(panel instanceof HTMLDetailsElement) || panel === except) continue;
      panel.open = false;
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #handleDocumentPointerDown = (event) => {
    if (!(event.target instanceof Node) || this.contains(event.target)) return;
    this.#closePanels();
  };

  /**
   * @param {PointerEvent} event
   */
  #handleDocumentPointerMove = (event) => {
    if (!desktopMediaQuery.matches) return;

    const movingDown = event.clientY > this.#lastPointerY;
    this.#lastPointerY = event.clientY;

    const openPanel = this.#openPanel;
    if (!openPanel) return;

    // Heading down toward the open panel: hold the switch so the panel doesn't change under the pointer.
    if (this.#pendingPanel && movingDown && performance.now() < this.#pendingDeadline) {
      this.#queueOpen(SWITCH_DELAY);
    }

    if (event.target instanceof Node) {
      if (openPanel.contains(event.target)) {
        this.#cancelClose();
        return;
      }

      if (this.#pendingPanel?.contains(event.target)) return;
    }

    this.#scheduleClose();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const activePanel = this.#openPanel;
    if (!activePanel) return;

    event.preventDefault();
    const hadFocus = activePanel.contains(document.activeElement);
    activePanel.open = false;
    if (hadFocus) activePanel.querySelector(':scope > summary')?.focus();
  };

  #handleBreakpointChange = () => {
    this.#cancelOpen();
    this.#closePanels();
    this.#syncOverflow();
  };

  /**
   * The menu has no overflow list, so items that don't fit would sit out of reach. Uses the drawer
   * until the window is wide enough for them.
   */
  #syncOverflow = () => {
    const header = this.closest('#header-component');
    const { scroller } = this.refs;
    if (!(header instanceof HTMLElement) || !scroller || !desktopMediaQuery.matches) return;

    if (this.#fitsAtWidth) {
      if (window.innerWidth < this.#fitsAtWidth) return;

      this.#fitsAtWidth = 0;
      header.removeAttribute('data-menu-overflow');
      setHeaderMenuStyle();
      return;
    }

    if (header.dataset.menuStyle !== 'menu') return;

    const overflow = scroller.scrollWidth - scroller.clientWidth;
    if (overflow <= 1) return;

    this.#fitsAtWidth = window.innerWidth + overflow;
    header.setAttribute('data-menu-overflow', '');
    this.#cancelOpen();
    this.#closePanels();
    setHeaderMenuStyle();
  };
}

if (!customElements.get('inchbug-mega-menu-component')) {
  customElements.define('inchbug-mega-menu-component', InchbugMegaMenuComponent);
}
